import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';
import { toast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';

interface CopyablePhoneNumberProps {
  number: string;
  className?: string;
}

const CopyablePhoneNumber: React.FC<CopyablePhoneNumberProps> = ({
  number,
  className,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copiedTimeout = useRef<number | undefined>(undefined);

  useEffect(() => {
    return () => window.clearTimeout(copiedTimeout.current);
  }, []);

  const handleCopy = async (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(number);
      setCopied(true);
      toast({
        title: t('agent.settings.phone_number.copied.translation', 'Copied'),
      });
      window.clearTimeout(copiedTimeout.current);
      copiedTimeout.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast({
        title: t(
          'agent.settings.phone_number.copy_failed.translation',
          'Could not copy'
        ),
      });
    }
  };

  const copyLabel = t('agent.settings.phone_number.copy.translation', {
    number,
    defaultValue: `Copy ${number}`,
  });

  return (
    <div
      className={cn(
        'flex h-9 w-full items-center gap-2 rounded-full border border-input bg-background px-3',
        className
      )}
    >
      <span className="min-w-0 flex-1 truncate text-sm text-foreground">
        {number}
      </span>
      <button
        type="button"
        onClick={handleCopy}
        className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        aria-label={copyLabel}
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 shrink-0 text-green-600" />
        ) : (
          <Copy className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        )}
      </button>
    </div>
  );
};

export default CopyablePhoneNumber;
